import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import { theme } from "../../../../Themes/Themes";
import useStore from "../../../../Store/Store";
import {
  getSimulationMappingsForPath,
  hasSimulationMappings,
} from "../../../../utils/SimulationMappingUtils";

interface SimulationButtonProps {
  startLine?: number;
  endLine?: number;
  lineHeight?: number; // Height of each line in pixels (default: 20px)
  containerRef?: React.RefObject<HTMLDivElement>; // Reference to the code container
}

const SimulationButton: React.FC<SimulationButtonProps> = ({
  startLine,
  endLine,
  lineHeight = 20,
  containerRef,
}) => {
  const [buttonPosition, setButtonPosition] = useState<{
    top: number;
    visible: boolean;
  }>({
    top: 0,
    visible: false,
  });

  const { currentPath, setDialog, simulationsState, isRunningSimulation } =
    useStore((state) => ({
      currentPath: state.currentPath,
      setDialog: state.setDialog,
      simulationsState: state.simulationsState,
      isRunningSimulation: state.isRunningSimulation,
    }));

  // Check if current code block has simulation mappings
  const simulationMappings = getSimulationMappingsForPath(currentPath);
  const hasSimulations = hasSimulationMappings(currentPath);

  // Calculate button position based on actual DOM elements
  useEffect(() => {
    const calculatePosition = () => {
      if (!containerRef?.current || !startLine || !hasSimulations) {
        setButtonPosition({ top: 0, visible: false });
        return;
      }

      // Always try to find the start line element by ID first for pixel-perfect positioning
      const startLineElement = document.getElementById("start-line-code");

      if (startLineElement && containerRef.current) {
        // Use the actual DOM element position for precise alignment
        const containerRect = containerRef.current.getBoundingClientRect();
        const lineRect = startLineElement.getBoundingClientRect();
        const relativeTop = lineRect.top - containerRect.top;

        // Position the button exactly at the center of the highlighted line
        setButtonPosition({
          top: Math.max(0, relativeTop + lineRect.height / 2 - 12 + 40), // Center on line, account for button height, shift down 40px
          visible:
            relativeTop >= 0 &&
            relativeTop <= (containerRef.current.clientHeight || 500),
        });
      } else {
        // Enhanced fallback: more precise calculation for code line positioning
        const containerElement = containerRef.current;
        const scrollTop = containerElement.scrollTop || 0;

        // More accurate header calculation - account for actual code content offset
        const codeContentStart = 30; // Typical padding/margin for code content
        const linePosition = codeContentStart + (startLine - 1) * lineHeight;
        const adjustedTop = linePosition - scrollTop + lineHeight / 2 - 12; // Center on line

        setButtonPosition({
          top: Math.max(0, adjustedTop + 40), // Add 40px shift down
          visible:
            adjustedTop >= 0 &&
            adjustedTop < (containerElement.clientHeight || 500),
        });
      }
    };

    // Calculate position immediately and on changes
    const timeoutId = setTimeout(calculatePosition, 50); // Small delay for DOM updates

    // Recalculate on scroll and resize
    const container = containerRef?.current;
    if (container) {
      container.addEventListener("scroll", calculatePosition);
      window.addEventListener("resize", calculatePosition);
      return () => {
        container.removeEventListener("scroll", calculatePosition);
        window.removeEventListener("resize", calculatePosition);
        clearTimeout(timeoutId);
      };
    }

    return () => clearTimeout(timeoutId);
  }, [startLine, containerRef, lineHeight, hasSimulations, currentPath]);

  // Don't render if no simulations are linked to this code
  if (!hasSimulations || !startLine) {
    return null;
  }

  // Don't show button if the only simulation linked is the currently running simulation
  if (
    isRunningSimulation &&
    simulationMappings.length === 1 &&
    simulationMappings[0].simulationName ===
      simulationsState.selectedSimulationKey
  ) {
    return null;
  }

  // Don't render if not visible (scrolled out of view)
  if (!buttonPosition.visible) {
    return null;
  }

  const handleSimulationClick = () => {
    if (simulationMappings.length === 1) {
      // If only one simulation mapping, go directly to it
      const mapping = simulationMappings[0];
      navigateToSimulation(mapping);
    } else if (simulationMappings.length > 1) {
      // If multiple simulation mappings, show selection dialog
      showSimulationSelectionDialog();
    }
  };

  const navigateToSimulation = (mapping: any) => {
    const store = useStore.getState();

    // First load the simulation by name (this handles tab switching and basic setup)
    store.loadSimulationByName(mapping.simulationName);

    // Get fresh state after loadSimulationByName
    const freshSimulationsState = useStore.getState().simulationsState;

    // Then set it to running mode and select the specific step
    store.setIsRunningSimulation(true);

    // Set the current step to the mapped simulation step in the simulationsState
    store.setSimulationsState({
      ...freshSimulationsState,
      currentStep: mapping.simStepId,
    });
  };

  const showSimulationSelectionDialog = () => {
    setDialog("DIALOG", {
      message: (
        <div>
          <div style={{ marginBottom: "16px" }}>
            This code block is referenced in multiple simulations. Which one
            would you like to open?
          </div>
          {simulationMappings.map((mapping, index) => {
            const isCurrentlyPlaying =
              isRunningSimulation &&
              mapping.simulationName === simulationsState.selectedSimulationKey;

            return (
              <div
                key={index}
                style={{
                  padding: "8px",
                  marginBottom: "8px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                  position: "relative",
                }}
                onClick={() => {
                  navigateToSimulation(mapping);
                  setDialog("");
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentlyPlaying) {
                    e.currentTarget.style.backgroundColor =
                      theme.custom.lighterPink;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{mapping.simulationName}</span>
                  {isCurrentlyPlaying && (
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "normal",
                        backgroundColor: theme.custom.darkGrey,
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "8px",
                      }}
                    >
                      Current Simulation
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {mapping.simStepLabel}
                </div>
              </div>
            );
          })}
        </div>
      ),
    });
  };

  return (
    <div
      style={{
        position: "absolute",
        top: `${buttonPosition.top}px`,
        right: "15vw", // Move left by 15vw from the right edge
        zIndex: 1001, // Higher than external links buttons
      }}
    >
      <Tooltip title="Open in simulation" placement="left" arrow>
        <IconButton
          onClick={handleSimulationClick}
          sx={{
            width: "24px",
            height: "24px",
            border: `2px solid ${theme.custom.lighterPink}`,
            borderRadius: "50%",
            backgroundColor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              backgroundColor: theme.custom.lighterPink,
              "& .MuiSvgIcon-root": {
                color: "white",
              },
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <ScienceIcon
            sx={{
              fontSize: "12px",
              color: theme.custom.lighterPink,
            }}
          />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default SimulationButton;
